import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { Card, Spinner } from '@viana-demos/ui';
import type { DemoDescriptor, DemoListResponse } from '@viana-demos/types';
import { apiGet } from '@core/http';
import { DEMO_REGISTRY } from '../registry';

const STATUS_STYLES: Record<string, string> = {
  live: 'bg-emerald-50 text-emerald-700',
  beta: 'bg-amber-50 text-amber-700',
  prototype: 'bg-slate-100 text-slate-600',
};

/**
 * The screen a superadmin lands on after signing in: pick which demo to run.
 *
 * The cards are the intersection of what this build can run (DEMO_REGISTRY) and what the server
 * says is enabled (GET /demos), so a half-finished demo can be hidden without a frontend release.
 */
export function DemoLauncher() {
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['demos'],
    queryFn: () => apiGet<DemoListResponse>('/demos'),
  });

  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Card className="max-w-md p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 h-8 w-8 text-amber-500" aria-hidden />
          <h2 className="font-semibold text-viana-primary">Could not load the demo list</h2>
          <p className="mt-2 text-sm text-viana-muted">
            The demos API did not answer. Nothing is shown rather than guessing which demos you are
            allowed to run.
          </p>
          <button
            onClick={() => refetch()}
            className="mt-6 text-sm font-medium text-viana-secondary hover:underline"
          >
            Try again
          </button>
        </Card>
      </div>
    );
  }

  const enabled = new Map<string, DemoDescriptor>(
    data.demos.filter((demo) => demo.enabled).map((demo) => [demo.id, demo]),
  );
  const available = DEMO_REGISTRY.filter((demo) => enabled.has(demo.id));

  return (
    <div className="mx-auto w-full max-w-5xl overflow-y-auto p-6 sm:p-10">
      <h1 className="text-2xl font-semibold text-viana-primary">Choose a demo</h1>
      <p className="mt-1 text-sm text-viana-muted">
        {available.length} demo{available.length === 1 ? '' : 's'} available to your account.
      </p>

      {available.length === 0 ? (
        <Card className="mt-8 p-8 text-center text-sm text-viana-muted">
          No demos are enabled for your account right now.
        </Card>
      ) : (
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {available.map((demo) => {
            const descriptor = enabled.get(demo.id)!;
            const Icon = demo.icon;
            return (
              <li key={demo.id}>
                <Link to={`/demo/${demo.id}`} className="block h-full">
                  <Card className="flex h-full flex-col p-6 hover:shadow-md">
                    <div className="flex items-start justify-between gap-3">
                      <span className="rounded-lg bg-viana-light p-2 text-viana-secondary">
                        <Icon className="h-5 w-5" aria-hidden />
                      </span>
                      {descriptor.status && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[descriptor.status] ?? STATUS_STYLES.prototype
                          }`}
                        >
                          {descriptor.status}
                        </span>
                      )}
                    </div>
                    <h2 className="mt-4 font-semibold text-viana-primary">
                      {descriptor.title || demo.title}
                    </h2>
                    <p className="mt-2 text-sm text-viana-muted">{descriptor.blurb || demo.blurb}</p>
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
